import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { AccessGroup, BackendAccessGroup } from '../shared/classes/access-group';
import { AccessGroupRule, BackendAccessGroupRule } from '../shared/classes/access-group-rule';
import { AccessGroupUser, BackEndAccessGroupUserList } from '../shared/classes/access-group-user';
import { AccessPolicy, BackEndAccessPolicy } from '../shared/classes/access-policy';
import { Account, ACCOUNT_MEDIA_TYPE, BackEndAccount } from '../shared/classes/account';
import { Application, BackEndApplication } from '../shared/classes/application';
import { ApplicationUser, BackEndApplicationUserList } from '../shared/classes/application-user';
import { BackEndCertificate, Certificate } from '../shared/classes/certificate';
import {
  BackEndClient,
  BackEndOauthClient,
  BackEndSamlClient,
  Client,
  OauthClient,
  SamlClient
} from '../shared/classes/client';
import { BackEndCredentials, BackEndCredentialsPatch, Credentials } from '../shared/classes/credentials';
import { BackEndDirectory, Directory, DIRECTORY_MEDIA_TYPE } from '../shared/classes/directory';
import { BackEndDnsVerification, DnsVerification, DnsVerificationStatus } from '../shared/classes/dns-verification';
import { BackEndDomain, BackEndDomainCollection, CORPORATE_DOMAIN_COLLECTION_MEDIA_TYPE, CORPORATE_DOMAIN_MEDIA_TYPE, Domain } from '../shared/classes/domain';
import { BackEndEnterprise, BackEndEnterprisePatch, Enterprise } from '../shared/classes/enterprise';
import {
  BackendRegistrationUrl,
  BackendRegistrationUrlRequest,
  RegistrationUrl, REGISTRATION_URL_MEDIA_TYPE
} from '../shared/classes/registration-url';
import { ClientType } from '../shared/enums/client-type.enum';
import { ContentType } from '../shared/enums/content-type.enum';
import { DirectoryType } from '../shared/enums/directory-type.enum';
import { EnterpriseStatus } from '../shared/enums/enterprise-status.enum';
import { HeaderKey } from '../shared/enums/header-key.enum';
import { UserStatus } from '../shared/enums/user-status.enum';
import { ConfigService, Endpoint } from '../shared/services/config';
import { DomainVerificationType } from '../shared/enums/domain-verification-type.enum';
import { BackEndInvitation, Invitation, INVITATION_MEDIA_TYPE } from '../shared/classes/invitation';
import { LoginService } from '../shared/services/login.service';
import { CORPORATE_DOMAIN_REGISTRATION_MEDIA_TYPE, DomainRegistrationUrl } from '../shared/classes/domain-registration-url';
import { IdentityStatusUpdate, IDENTITY_STATUS_UPDATE_MEDIA_TYPE } from '../shared/classes/identity-status-update';
import { BackendIdentity, Identity, IDENTITY_MEDIA_TYPE } from '../shared/classes/identity';
import { CorporateEmailStatus, CORPORATE_EMAIL_STATUS_MEDIA_TYPE } from '../shared/classes/corporate-email-status';
import { AccountDetails, ACCOUNT_DETAILS_MEDIA_TYPE, BackEndAccountDetails } from '../shared/classes/account-details';
import { AccountPermissions, ACCOUNT_PERMISSIONS_MEDIA_TYPE, BackEndAccountPermissions } from '../shared/classes/account-permissions';
import { BackEndRealmHierarchy, RealmHierarchy, REALM_HIERARCHY_MEDIA_TYPE } from '../shared/classes/realm-hierarchy';
import { AuditRecord, AUDIT_RECORD_MEDIA_TYPE, BackEndAuditRecord } from '../shared/classes/audit-record';
import { BackEndPagedResults, PagedResults } from '../shared/classes/paged-results';
import { AuditQuery } from '../shared/classes/audit-query';


@Injectable({
  providedIn: 'root',
})
export class CrudService {
  private readonly configService = inject(ConfigService);
  private readonly httpClient = inject(HttpClient);
  readonly login = inject(LoginService);

  private readonly url = {
    accountsUrl: `/accounts`,
    auditsUrl: `/audits`,
    certificateUrl: `/certificates`,
    credentialsUrl: `/credentials`,
    domainsUrl: `/domains`,
    enterprisesUrl: `/enterprises`,
    realmsUrl: `/realms`,
  };
  private accessToken = this.login.user?.realmAccessToken;

  constructor() {
    const adminServiceUrl = this.configService.getEndpoint(Endpoint.AdminService);
    Object.keys(this.url).forEach(key => {
      const url = this.url[key];
      this.url[key] = `${adminServiceUrl}${url}`;
    });

    this.login.userChanged.subscribe(user => {
      this.accessToken = user?.realmAccessToken;
    });
  }

  public createAccount(account: Account): Observable<void> {
    const url = this.url.accountsUrl;
    const body: BackEndAccount = account.toBackEnd();
    const headers = {
      [HeaderKey.Authorization]: this.accessToken?.toString(),
      [HeaderKey.ContentType]: ACCOUNT_MEDIA_TYPE,
    };
    return this.httpClient.post<void>(url, body, { headers });
  }

  public createAccountResend(accountId: string): Observable<void> {
    const url = `${this.url.accountsUrl}/${accountId}/resend`;
    const headers = {
      [HeaderKey.Authorization]: this.accessToken?.toString(),
      [HeaderKey.ContentType]: ContentType.Resend,
    };
    return this.httpClient.post<void>(url, {}, { headers });
  }

  public createCertificate(certificate: Certificate): Observable<void> {
    const url = this.url.certificateUrl;
    const body: BackEndCertificate = certificate.toBackEnd();
    const headers = {
      [HeaderKey.Authorization]: this.accessToken?.toString(),
      [HeaderKey.ContentType]: ContentType.Certificate,
    };
    return this.httpClient.post<void>(url, body, { headers });
  }

  public createClient(enterpriseId: string, applicationId: string, client: Client): Observable<Client> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}/applications/${applicationId}/clients`;
    const body: BackEndClient = client.toBackEnd();
    const headers = {
      [HeaderKey.Authorization]: this.accessToken?.toString(),
      [HeaderKey.ContentType]: ContentType.Client,
    };
    return this.httpClient.post<BackEndClient>(url, body, { headers }).pipe(
      map(response => this.parseClient(response)),
    );
  }

  public createCredentials(credentials: Credentials): Observable<Credentials> {
    const url = this.url.credentialsUrl;
    const body: BackEndCredentials = credentials.toBackEnd();
    const headers = {
      [HeaderKey.Authorization]: this.accessToken?.toString(),
      [HeaderKey.ContentType]: ContentType.Credentials,
    };
    return this.httpClient.post<BackEndCredentials>(url, body, { headers }).pipe(
      map((backEndCredentials: BackEndCredentials) => new Credentials(backEndCredentials)),
    );
  }

  public createEnterprise(enterprise: Enterprise): Observable<void> {
    const url = this.url.enterprisesUrl;
    const body: BackEndEnterprise = enterprise.toBackEnd();
    const headers = {
      [HeaderKey.Authorization]: this.accessToken?.toString(),
      [HeaderKey.ContentType]: ContentType.Enterprise,
    };
    return this.httpClient.post<void>(url, body, { headers });
  }

  public getAccount(accountId: string): Observable<AccountDetails> {
    const url = `${this.url.accountsUrl}/${accountId}`;
    const headers = {
      [HeaderKey.Accept]: ACCOUNT_DETAILS_MEDIA_TYPE,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };
    return this.httpClient.get<BackEndAccountDetails>(url, { headers }).pipe(
      map((backEndAccount: BackEndAccountDetails) => new AccountDetails(backEndAccount)),
    );
  }

  public getAccountPermissions(accountId: string, realmId: string): Observable<AccountPermissions> {
    const url = `${this.url.accountsUrl}/${accountId}/realms/${realmId}/permissions`;
    const headers = {
      [HeaderKey.Accept]: ACCOUNT_PERMISSIONS_MEDIA_TYPE,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };
    return this.httpClient.get<BackEndAccountPermissions>(url, { headers }).pipe(
      map((backEndPermissions: BackEndAccountPermissions) => new AccountPermissions(backEndPermissions)),
    );
  }

  public getAccounts(options?: { realm?: string; disabled?: boolean }): Observable<AccountDetails[]> {
    const url = this.url.accountsUrl;
    const headers = {
      [HeaderKey.Accept]: ACCOUNT_DETAILS_MEDIA_TYPE,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };

    return this.httpClient.get<BackEndAccountDetails[]>(url, { headers, params: options }).pipe(
      map<BackEndAccountDetails[], AccountDetails[]>(accounts => accounts.map(backEndAccount => new AccountDetails(backEndAccount))),
      map<AccountDetails[], AccountDetails[]>(accounts => accounts.sort((a, b) => {
        const aOrder = a.status === UserStatus.Active ? 0 : 1;
        const bOrder = b.status === UserStatus.Active ? 0 : 1;
        return aOrder - bOrder;
      })),
    );
  }

  public getCertificates(): Observable<Certificate[]> {
    const url = this.url.certificateUrl;
    const headers = {
      [HeaderKey.Accept]: ContentType.Certificate,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };
    return this.httpClient.get<BackEndCertificate[]>(url, { headers }).pipe(
      map(certificates => certificates.map(certificate => new Certificate(certificate))),
    );
  }

  public getClient(enterpriseId: string, applicationId: string, clientId: string): Observable<Client> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}`
      + `/applications/${applicationId}/clients/${clientId}`;
    const headers = {
      [HeaderKey.Accept]: ContentType.Client,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };
    return this.httpClient.get<BackEndClient>(url, { headers }).pipe(
      map(client => this.parseClient(client)),
    );
  }

  public getClients(enterpriseId: string, applicationId: string): Observable<Client[]> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}`
      + `/applications/${applicationId}/clients`;
    const headers = {
      [HeaderKey.Accept]: ContentType.Client,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };
    return this.httpClient.get<BackEndClient[]>(url, { headers }).pipe(
      map(clients => clients.map(client => this.parseClient(client))),
    );
  }

  public getClientMetadata(idpEntityId: string): Observable<string> {
    return this.httpClient.get<string>(idpEntityId, {responseType: 'text' as 'json'});
  }

  public getPowerShellScript(idpEntityId: string): Observable<string> {
    const url = `${idpEntityId}/powershell`;
    return this.httpClient.get<string>(url, {responseType: 'text' as 'json'});
  }

  public getCredentials(): Observable<Credentials[]> {
    const url = this.url.credentialsUrl;
    const headers = {
      [HeaderKey.Accept]: ContentType.Credentials,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };
    const params = {
      disabled: 'false',
    };
    return this.httpClient.get<BackEndCredentials[]>(url, { headers, params }).pipe(
      map((credentials: BackEndCredentials[]) => credentials.map(
        (backEndCredentials: BackEndCredentials) => new Credentials(backEndCredentials),
      )),
    );
  }

  public getEnterprise(enterpriseId: string): Observable<Enterprise> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}`;
    const headers = {
      [HeaderKey.Accept]: ContentType.Enterprise,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };
    return this.httpClient.get<BackEndEnterprise>(url, { headers }).pipe(
      map((backEndEnterprise: BackEndEnterprise) => new Enterprise(backEndEnterprise)),
    );
  }

  public getEnterprises(): Observable<Enterprise[]> {
    const url = this.url.enterprisesUrl;
    const headers = {
      [HeaderKey.Accept]: ContentType.Enterprise,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };
    return this.httpClient.get<BackEndEnterprise[]>(url, { headers }).pipe(
      map((enterprises: BackEndEnterprise[]) => enterprises.map(
        (backEndEnterprise: BackEndEnterprise) => new Enterprise(backEndEnterprise),
      )),
      map((enterprises: Enterprise[]) => {
        const [activeEnterprises, disabledEnterprises] = enterprises.reduce(([active, disabled], enterprise) => {
          return enterprise.status === EnterpriseStatus.Active ? [[...active, enterprise], disabled] : [active, [...disabled, enterprise]];
        }, [[], []]);
        return [...activeEnterprises, ...disabledEnterprises];
      }),
    );
  }

  public getRealmHierarchy(): Observable<RealmHierarchy> {
    const url = this.url.realmsUrl;
    const headers = {
      [HeaderKey.Accept]: REALM_HIERARCHY_MEDIA_TYPE,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };
    return this.httpClient.get<BackEndRealmHierarchy>(url, { headers }).pipe(
      map((backEndRealmHierarchy: BackEndRealmHierarchy) => new RealmHierarchy(backEndRealmHierarchy)),
    );
  }

  public updateAccountDetails(account: AccountDetails): Observable<void> {
    const url = `${this.url.accountsUrl}/${account.id}`;
    const body = account.toBackEndPatch();
    const headers = {
      [HeaderKey.Authorization]: this.accessToken?.toString(),
      [HeaderKey.ContentType]: ACCOUNT_DETAILS_MEDIA_TYPE,
    };
    return this.httpClient.patch<void>(url, body, { headers });
  }

  public updateAccountPermissions(permissions: AccountPermissions): Observable<void> {
    const url = `${this.url.accountsUrl}/${permissions.accountId}/realms/${permissions.realmId}/permissions`;
    const body = permissions.toBackEnd();
    const headers = {
      [HeaderKey.Authorization]: this.accessToken?.toString(),
      [HeaderKey.ContentType]: ACCOUNT_PERMISSIONS_MEDIA_TYPE,
    };
    return this.httpClient.patch<void>(url, body, { headers });
  }

  public resetAccount(accountId: string): Observable<void> {
    const url = `${this.url.accountsUrl}/${accountId}/reset`;
    const headers = {
      [HeaderKey.Authorization]: this.accessToken?.toString(),
      [HeaderKey.ContentType]: ContentType.Reset,
    };
    return this.httpClient.post<void>(url, null, { headers });
  }

  public updateClient(enterpriseId: string, applicationId: string, client: Client): Observable<void> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}`
      + `/applications/${applicationId}/clients/${client.id}`;
    const body = client.toBackEnd();
    const headers = {
      [HeaderKey.Authorization]: this.accessToken?.toString(),
      [HeaderKey.ContentType]: ContentType.Client,
    };
    return this.httpClient.patch<void>(url, body, { headers });
  }

  public updateCredentials(credentials: Credentials): Observable<void> {
    const url = `${this.url.credentialsUrl}/${credentials.id}`;
    const body: BackEndCredentialsPatch = credentials.toBackEndPatch();
    const headers = {
      [HeaderKey.Authorization]: this.accessToken?.toString(),
      [HeaderKey.ContentType]: ContentType.Credentials,
    };
    return this.httpClient.patch<void>(url, body, { headers });
  }

  public updateEnterprise(enterprise: Enterprise): Observable<void> {
    const url = `${this.url.enterprisesUrl}/${enterprise.id}`;
    const body: BackEndEnterprisePatch = enterprise.toBackEndPatch();
    const headers = {
      [HeaderKey.Authorization]: this.accessToken?.toString(),
      [HeaderKey.ContentType]: ContentType.Enterprise,
    };
    return this.httpClient.patch<void>(url, body, { headers });
  }

  public updateEnterpriseStatus(enterpriseId: string, status: EnterpriseStatus): Observable<void> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}`;
    const body: BackEndEnterprisePatch = { status };
    const headers = {
      [HeaderKey.Authorization]: this.accessToken?.toString(),
      [HeaderKey.ContentType]: ContentType.Enterprise,
    };
    return this.httpClient.patch<void>(url, body, { headers });
  }

  public refreshClientSecret(enterpriseId: string, applicationId: string, clientId: string): Observable<Client> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}`
      + `/applications/${applicationId}/clients/${clientId}/secret`;
    const headers = {
      [HeaderKey.Authorization]: this.accessToken?.toString(),
      [HeaderKey.ContentType]: ContentType.SecretRefresh,
    };
    return this.httpClient.post<BackEndClient>(url, null, { headers }).pipe(
      map(client => this.parseClient(client)),
    );
  }

  /**
   * Delete a client from an application.
   * @param enterpriseId The enterprise that owns the application and client
   * @param applicationId The application the client is assigned to
   * @param clientId The client to delete
   * @returns The empty response observable
   */
  public deleteClient(enterpriseId: string, applicationId: string, clientId: string): Observable<void> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}`
      + `/applications/${applicationId}/clients/${clientId}`;
    const headers = {
      [HeaderKey.Accept]: ContentType.Client,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };
    return this.httpClient.delete<void>(url, { headers });
  }

  /**
   * Delete all clients from an application.
   * @param enterpriseId The enterprise that owns the application
   * @param applicationId The application to remove all clients from
   * @returns The empty response observable
   */
  public deleteAllClients(enterpriseId: string, applicationId: string): Observable<void> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}`
      + `/applications/${applicationId}/clients`;
    const headers = {
      [HeaderKey.Accept]: ContentType.Client,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };
    return this.httpClient.delete<void>(url, { headers });
  }

  //#region Applications
  public createApplication(enterpriseId: string, application: Application): Observable<void> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}/applications`;
    // TODO screen that the application should have no ID defined, or else error?
    const body: BackEndApplication = application.toBackEnd();
    const headers = {
      [HeaderKey.Authorization]: this.accessToken?.toString(),
      [HeaderKey.ContentType]: ContentType.Application,
    };
    return this.httpClient.post<void>(url, body, { headers });
  }

  public getApplication(enterpriseId: string, applicationId: string): Observable<Application> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}/applications/${applicationId}`;
    const headers = {
      [HeaderKey.Accept]: ContentType.Application,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };
    return this.httpClient.get<BackEndApplication>(url, { headers }).pipe(
      map(app => new Application(app)),
    );
  }

  public getApplications(enterpriseId: string): Observable<Application[]> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}/applications`;
    const headers = {
      [HeaderKey.Accept]: ContentType.Application,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };
    return this.httpClient.get<BackEndApplication[]>(url, { headers }).pipe(
      map(applications => applications.map(app => new Application(app))),
      map(applications => applications.sort((a, b) => a.name.localeCompare(b.name)))
    );
  }

  public getApplicationUsers(enterpriseId: string, applicationId: string): Observable<ApplicationUser[]> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}/applications/${applicationId}/users`;
    const headers = {
      [HeaderKey.Accept]: ContentType.ApplicationUsers,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };
    return this.httpClient.get<BackEndApplicationUserList>(url, { headers }).pipe(
      map(response => response.users),
      map(users => users.map(user => new ApplicationUser(user))),
    );
  }

  public updateApplication(enterpriseId: string, application: Application): Observable<void> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}/applications/${application.id}`;
    const body = application.toBackEnd();
    const headers = {
      [HeaderKey.Authorization]: this.accessToken?.toString(),
      [HeaderKey.ContentType]: ContentType.Application,
    };
    return this.httpClient.patch<void>(url, body, { headers });
  }

  /**
   * Delete an application and all of its rules.
   * The application MUST NOT have any access policies attached. If it has any, call {@link deleteAllAccessPolicies} first.
   * @param enterpriseId The enterprise that owns the application
   * @param applicationId The application to delete
   */
  public deleteApplication(enterpriseId: string, applicationId: string): Observable<void> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}/applications/${applicationId}`;
    const headers = {
      [HeaderKey.Accept]: ContentType.Application,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };
    return this.httpClient.delete<void>(url, { headers });
  }
  ////#endregion Applications

  //#region Registration URLs
  public createRegistrationUrl(enterpriseId: string, days: number): Observable<RegistrationUrl> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}/urls`;
    const body: BackendRegistrationUrlRequest = {
      num_days: days,
    };
    const headers = {
      [HeaderKey.ContentType]: REGISTRATION_URL_MEDIA_TYPE,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };
    return this.httpClient.post<BackendRegistrationUrl>(url, body, { headers }).pipe(
      map(regUrl => new RegistrationUrl(regUrl))
    );
  }

  public getRegistrationUrls(enterpriseId: string): Observable<RegistrationUrl[]> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}/urls`;
    const headers = {
      [HeaderKey.Accept]: REGISTRATION_URL_MEDIA_TYPE,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };
    return this.httpClient.get<BackendRegistrationUrl[]>(url, { headers }).pipe(
      map(regUrls => regUrls.map(regUrl => new RegistrationUrl(regUrl)))
    );
  }

  public deleteRegistrationUrl(enterpriseId: string, urlCode: string): Observable<HttpResponse<any>> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}/urls/${urlCode}`;
    const headers = {
      [HeaderKey.Accept]: REGISTRATION_URL_MEDIA_TYPE,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };
    return this.httpClient.delete(url, { headers, observe: 'response' });
  }

  public getDomainRegistrationUrls(enterpriseId: string): Observable<DomainRegistrationUrl[]> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}/domains`;
    const headers = {
      [HeaderKey.Accept]: CORPORATE_DOMAIN_COLLECTION_MEDIA_TYPE,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };

    const webAppUrl = this.configService.getIdaaswebappUrl();

    return this.httpClient.get<BackEndDomainCollection>(url, { headers }).pipe(
      map(domainCollection => domainCollection.domains),
      map(domainRegistrations => domainRegistrations.map(domain => new DomainRegistrationUrl(domain))),
      map(domainRegistrations => domainRegistrations.sort((a, b) => a.domain.localeCompare(b.domain))),
      tap(domains => domains.forEach(domain => domain.buildRegistrationUrl(webAppUrl))),
    );
  }

  public updateDomainRegistrationUrl(enterpriseId: string, registrationUrl: DomainRegistrationUrl): Observable<void> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}/domains/${registrationUrl.id}`;
    const body = registrationUrl.toBackEndPatch();
    const headers = {
      [HeaderKey.ContentType]: CORPORATE_DOMAIN_REGISTRATION_MEDIA_TYPE,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };

    return this.httpClient.patch<void>(url, body, { headers });
  }

  public createRegistrationUrlInvites(
    enterpriseId: string, registrationUrl: DomainRegistrationUrl, emails: string[]): Observable<void> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}/domains/${registrationUrl.id}/invite`;
    const headers = {
      [HeaderKey.ContentType]: ContentType.RegistrationInvite,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };
    return this.httpClient.post<void>(url, { email_addresses: emails }, { headers });
  }
  //#endregion Registration URLs

  //#region Access policies
  public getAccessPoliciesForApplication(enterpriseId: string, applicationId: string): Observable<AccessPolicy[]> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}/access?applicationId=${applicationId}`;
    const headers = {
      [HeaderKey.Accept]: ContentType.ApplicationAccess,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };
    return this.httpClient.get<BackEndAccessPolicy[]>(url, { headers }).pipe(
      map(policies => policies.map(policy => new AccessPolicy(policy))),
    );
  }

  public getAccessPoliciesForGroup(enterpriseId: string, groupId: string): Observable<AccessPolicy[]> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}/access?groupId=${groupId}`;
    const headers = {
      [HeaderKey.Accept]: ContentType.AccessPolicy,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };
    return this.httpClient.get<BackEndAccessPolicy[]>(url, { headers }).pipe(
      map(policies => policies.map(policy => new AccessPolicy(policy))),
    );
  }

  public createAccessPolicy(enterpriseId: string, accessPolicy: AccessPolicy): Observable<void> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}/access`;
    const body = accessPolicy.toBackEnd();
    const headers = {
      [HeaderKey.Authorization]: this.accessToken?.toString(),
      [HeaderKey.ContentType]: ContentType.ApplicationAccess,
    };
    return this.httpClient.post<void>(url, body, { headers });
  }

  /**
   * Delete the access policy that joins a specific group to a specific application.
   * @param enterpriseId The enterprise that owns the access policy
   * @param accessPolicy The access policy to delete
   * @returns The empty response observable
   */
  public deleteAccessPolicy(enterpriseId: string, accessPolicy: AccessPolicy): Observable<void> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}/access`
      + `?groupId=${accessPolicy.groupId}`
      + `&applicationId=${accessPolicy.applicationId}`;
    const headers = {
      [HeaderKey.Accept]: ContentType.ApplicationAccess,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };
    return this.httpClient.delete<void>(url, { headers });
  }

  /**
   * Delete ALL access policies from a given access group or application.
   * @param enterpriseId The enterprise that owns the access policies
   * @param source The access group OR application to delete all the policies from
   * @returns The empty response observable
   */
  public deleteAllAccessPolicies(enterpriseId: string, source: { accessGroupId: string } | { applicationId: string }): Observable<void> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}/access`;
    const params: { groupId?: string, applicationId?: string } = { };
    const accessGroupId = source['accessGroupId'];
    const applicationId = source['applicationId'];
    if (accessGroupId) {
      params.groupId = accessGroupId;
    } else if (applicationId) {
      params.applicationId = applicationId;
    }
    const headers = {
      [HeaderKey.Accept]: ContentType.ApplicationAccess,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };
    return this.httpClient.delete<void>(url, { params, headers });
  }
  //#endregion Access policies

  //#region Access Group
  public getAccessGroups(enterpriseId: string): Observable<AccessGroup[]> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}/accessgroups`;
    const headers = {
      [HeaderKey.Accept]: ContentType.AccessGroup,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };
    return this.httpClient.get<BackendAccessGroup[]>(url, { headers }).pipe(
      map(groups => groups.map(group => new AccessGroup(group))),
      map(groups => groups.sort((a, b) => a.name.localeCompare(b.name)))
    );
  }

  public getAccessGroup(enterpriseId: string, groupId: string): Observable<AccessGroup> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}/accessgroups/${groupId}`;
    const headers = {
      [HeaderKey.Accept]: ContentType.AccessGroup,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };
    return this.httpClient.get<BackendAccessGroup>(url, { headers }).pipe(
      map(group => new AccessGroup(group)),
    );
  }

  public getAccessGroupUsers(enterpriseId: string, groupId: string): Observable<AccessGroupUser[]> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}/accessgroups/${groupId}/users`;
    const headers = {
      [HeaderKey.Accept]: ContentType.AccessGroupUsers,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };
    return this.httpClient.get<BackEndAccessGroupUserList>(url, { headers }).pipe(
      map(response => response.users),
      map(users => users.map(user => new AccessGroupUser(user))),
    );
  }

  public createApplicationUsers(
    enterpriseId: string, regUrl: string, emails: string[]): Observable<void> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}/urls/${regUrl}/invite`;
    const headers = {
      [HeaderKey.ContentType]: ContentType.RegistrationInvite,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };
    return this.httpClient.post<void>(url, { email_addresses: emails }, { headers });
  }

  public createAccessGroup(enterpriseId: string, group: AccessGroup): Observable<void> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}/accessgroups`;
    const body = group.toBackend();
    const headers = {
      [HeaderKey.ContentType]: ContentType.AccessGroup,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };
    return this.httpClient.post<void>(url, body, { headers });
  }

  public updateAccessGroup(enterpriseId: string, groupId: string, updatedGroup: AccessGroup): Observable<void> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}/accessgroups/${groupId}`;
    const body = updatedGroup.toBackend();
    const headers = {
      [HeaderKey.ContentType]: ContentType.AccessGroup,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };
    return this.httpClient.patch<void>(url, body, { headers });
  }

  /**
   * Delete an access group and all of its rules.
   * The access group MUST NOT have any access policies attached. If it has any, call {@link deleteAllAccessPolicies} first.
   * @param enterpriseId The enterprise that owns the access group
   * @param groupId The access group to delete
   */
  public deleteAccessGroup(enterpriseId: string, groupId: string): Observable<void> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}/accessgroups/${groupId}`;
    const headers = {
      [HeaderKey.Accept]: ContentType.AccessGroup,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };
    return this.httpClient.delete<void>(url, { headers });
  }
  //#endregion Access Group

  //#region Directories

  public createDirectory(enterpriseId: string, directory: Directory): Observable<Directory> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}/directories`;
    const headers = {
      [HeaderKey.ContentType]: DIRECTORY_MEDIA_TYPE,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };

    return this.httpClient.post<BackEndDirectory>(url, directory.toBackend(), { headers })
      .pipe(
        map(returnedDirectory => new Directory(returnedDirectory))
      );
  }

  /**
   * Get the set of directories belonging to an enterprise from the server.
   *
   * @param enterpriseId The enterprise whose directories to request
   * @param type The directory type to filter down to, if any.
   *
   * @returns The enterprise's directories (possibly filtered).
   */
  public getDirectories(enterpriseId: string, type?: DirectoryType): Observable<Directory[]> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}/directories`;
    const headers = {
      [HeaderKey.Accept]: DIRECTORY_MEDIA_TYPE,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };
    const params = type ? { type } : undefined;
    return this.httpClient.get<BackEndDirectory[]>(url, { headers, params }).pipe(
      map(directories => directories.map(directory => new Directory(directory))),
    );
  }

  public getDirectory(enterpriseId: string, directoryId: string): Observable<Directory> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}/directories/${directoryId}`;
    const headers = {
      [HeaderKey.Accept]: DIRECTORY_MEDIA_TYPE,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };
    return this.httpClient.get<BackEndDirectory>(url, { headers }).pipe(
        map(directory => new Directory(directory)),
      );
  }

  public getDirectoryAccessGroups(enterpriseId: string, directoryId: string): Observable<AccessGroup[]> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}/directories/${directoryId}/accessgroups`;
    const headers = {
      [HeaderKey.Accept]: ContentType.AccessGroup,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };
    return this.httpClient.get<BackendAccessGroup[]>(url, { headers }).pipe(
      map(groups => groups.map(group => new AccessGroup(group))),
      map(groups => groups.sort((a, b) => a.name.localeCompare(b.name)))
    );
  }

  public updateDirectory(enterpriseId: string, directoryId: string, directory: Directory): Observable<void> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}/directories/${directoryId}`;
    const params = {
      type: directory.type,
    };
    const headers = {
      [HeaderKey.ContentType]: DIRECTORY_MEDIA_TYPE,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };

    return this.httpClient.patch<void>(url, directory.toBackendPatch(), {headers, params});
  }

  public deleteDirectory(enterpriseId: string, directoryId: string): Observable<void> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}/directories/${directoryId}`;
    const headers = {
      [HeaderKey.Accept]: DIRECTORY_MEDIA_TYPE,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };

    return this.httpClient.delete<void>(url, {headers});
  }
  //#endregion Directories

  //#region Group Rules
  public getGroupRules(enterpriseId: string, groupId: string): Observable<AccessGroupRule[]> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}/accessgroups/${groupId}/rules`;
    const headers = {
      [HeaderKey.Accept]: ContentType.AccessGroupRule,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };
    return this.httpClient.get<BackendAccessGroupRule[]>(url, { headers }).pipe(
      map(rules => rules.map(rule => new AccessGroupRule(rule))),
    );
  }

  public createGroupRule(enterpriseId: string, groupId: string, rule: AccessGroupRule) {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}/accessgroups/${groupId}/rules`;
    const headers = {
      [HeaderKey.ContentType]: ContentType.AccessGroupRule,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };
    return this.httpClient.post<void>(url, [rule.toBackend()], { headers });
  }

  public deleteGroupRule(enterpriseId: string, groupId: string, ruleId: string) {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}/accessgroups/${groupId}/rules/${ruleId}`;
    const headers = {
      [HeaderKey.Accept]: ContentType.AccessGroupRule,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };
    return this.httpClient.delete<void>(url, { headers });
  }
  //#endregion Group Rules

  //#region Corporate Domains
  public createDomain(enterpriseId: string, domain: Domain): Observable<Domain> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}/domains`;
    const body: BackEndDomain = domain.toBackEnd();
    const headers = {
      [HeaderKey.Authorization]: this.accessToken?.toString(),
      [HeaderKey.ContentType]: CORPORATE_DOMAIN_MEDIA_TYPE,
    };
    return this.httpClient.post<BackEndDomain>(url, body, { headers }).pipe(
      map((backEndDomain: BackEndDomain) => new Domain(backEndDomain)),
    );
  }

  /**
   * Get a list of corporate domains belonging to an enterprise. The list can be domains of any status.
   * @param enterpriseId The enterprise whose domains are being requested
   */
  public getDomains(enterpriseId: string): Observable<Domain[]> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}/domains`;
    const headers = {
      [HeaderKey.Accept]: CORPORATE_DOMAIN_MEDIA_TYPE,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };
    return this.httpClient.get<BackEndDomain[]>(url, { headers }).pipe(
      map((domains: BackEndDomain[]) => domains.map(domain => new Domain(domain))),
    );
  }

  /**
   * Get a simple list of approved corporate domains.
   * @param enterpriseId The enterprise whose domains are being requested
   * @param types The verification types to filter down to
   */
  public getApprovedDomains(enterpriseId: string, types?: DomainVerificationType[]): Observable<string[]> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}/domains`;
    const headers = {
      [HeaderKey.Accept]: CORPORATE_DOMAIN_COLLECTION_MEDIA_TYPE,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };
    const params = types ? {type: this.toQueryParamList(types)} : {};
    return this.httpClient.get<BackEndDomainCollection>(url, { headers, params }).pipe(
      map(domainCollection => domainCollection.domains),
      map(domainRegistrations => domainRegistrations.map(domain => domain.domain)),
    );
  }

  public deleteDomain(enterpriseId: string, corporateDomainId: string): Observable<void> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}/domains/${corporateDomainId}`;
    const headers = {
      [HeaderKey.Accept]: CORPORATE_DOMAIN_MEDIA_TYPE,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };
    return this.httpClient.delete<void>(url, { headers });
  }

  public getDnsVerificationCode(enterpriseId: string, corporateDomainId: string): Observable<string> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}/domains/${corporateDomainId}/verify`;
    const headers = {
      [HeaderKey.Accept]: ContentType.DnsVerification,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };
    return this.httpClient.get<BackEndDnsVerification>(url, { headers }).pipe(
      map(verification => new DnsVerification(verification).verificationCode));
  }

  public verifyDomainByDns(enterpriseId: string, corporateDomainId: string): Observable<DnsVerificationStatus> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}/domains/${corporateDomainId}/verify`;
    const headers = {
      [HeaderKey.ContentType]: ContentType.DnsVerification,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };
    return this.httpClient.patch<BackEndDnsVerification>(url, {}, { headers }).pipe(
      map(verification => new DnsVerification(verification).status));
  }
  //#endregion Corporate Domains

  //#region Corporate emails
  /**
   * Create an invitation for a user.
   * @param enterpriseId The enterprise the invitation is created within
   * @param invitation The invitation object with the following required properties:
   * @param invitation.personalPhone The invited individual's contact phone number
   * @param invitation.personalEmail The invited individual's alternative email
   * @param invitation.corporateEmail The corporate email to create for the invited individual
   * @param invitation.lineManagerEmail The contact email for the line manager of the invited individual.
   *   Cannot be the same as the logged in user.
   * @returns The URL of the invitation, at `{adminService}/enterprises/:enterpriseId/invitations/:invitationId`
   */
  public createCorporateInvitation(enterpriseId: string, invitation: Invitation): Observable<string> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}/invitations`;
    const body = invitation.toBackend();
    const headers = {
      [HeaderKey.ContentType]: INVITATION_MEDIA_TYPE,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };
    return this.httpClient.post<void>(url, body, {
      headers: headers,
      observe: 'response'
    }).pipe(
      map(response => response.headers.get('location'))
    );
  }

  public getInvitation(enterpriseId: string, invitationId: string): Observable<Invitation> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}/invitations/${invitationId}`;
    const headers = {
      [HeaderKey.Accept]: INVITATION_MEDIA_TYPE,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };

    return this.httpClient.get<BackEndInvitation>(url, { headers }).pipe(
      map((invitation: BackEndInvitation) => new Invitation(invitation)),
    );
  }

  public getInvitations(enterpriseId: string): Observable<Invitation[]> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}/invitations`;
    const headers = {
      [HeaderKey.Accept]: INVITATION_MEDIA_TYPE,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };

    return this.httpClient.get<BackEndInvitation[]>(url, { headers }).pipe(
      map((invitations: BackEndInvitation[]) => invitations.map(invitation => new Invitation(invitation))),
    );
  }

  public deleteInvitation(enterpriseId: string, invitationId: string): Observable<void> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}/invitations/${invitationId}`;
    const headers = {
      [HeaderKey.Accept]: INVITATION_MEDIA_TYPE,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };
    return this.httpClient.delete<void>(url, { headers });
  }

  public updateCorporateEmailStatus(enterpriseId: string,  corporateEmailStatus: CorporateEmailStatus): Observable<void> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}/emails/${corporateEmailStatus.email}`;
    const body = corporateEmailStatus.toBackEndPatch();
    const headers = {
      [HeaderKey.ContentType]: CORPORATE_EMAIL_STATUS_MEDIA_TYPE,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };
    return this.httpClient.patch<void>(url, body, { headers });
  }

  //#endregion Corporate emails

  //#region Blocked User management
  public lookupIdentityByEmail(enterpriseId: string, email: string): Observable<Identity> {
    return this.lookupIdentity(enterpriseId, { email });
  }

  public lookupIdentityByPhone(enterpriseId: string, phone: string): Observable<Identity> {
    return this.lookupIdentity(enterpriseId, { phone });
  }

  public lookupIdentity(enterpriseId: string, identifier: { phone: string } | { email: string }): Observable<Identity> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}/identities`;
    const headers = {
      [HeaderKey.Accept]: IDENTITY_MEDIA_TYPE,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };
    const params = {
      ...identifier
    };
    return this.httpClient.get<BackendIdentity>(url, { headers, params }).pipe(
      map((backendIdentity: BackendIdentity) => new Identity(backendIdentity)),
    );
  }

  public updateIdentityStatus(enterpriseId: string, identityStatusUpdate: IdentityStatusUpdate): Observable<void> {
    const url = `${this.url.enterprisesUrl}/${enterpriseId}/identities`;
    const body = identityStatusUpdate.toBackendPatch();
    const headers = {
      [HeaderKey.Authorization]: this.accessToken?.toString(),
      [HeaderKey.ContentType]: IDENTITY_STATUS_UPDATE_MEDIA_TYPE,
    };
    return this.httpClient.patch<void>(url, body, { headers });
  }
  //#endregion

  public getAuditRecords(query: AuditQuery, page: number, size: number): Observable<PagedResults<AuditRecord, BackEndAuditRecord>> {
    const url = `${this.url.auditsUrl}`;
    const headers = {
      [HeaderKey.Accept]: AUDIT_RECORD_MEDIA_TYPE,
      [HeaderKey.Authorization]: this.accessToken?.toString(),
    };
    let params = query.toParams();
    params = params.set('page', page);
    params = params.set('size', size);

    return this.httpClient.get<BackEndPagedResults<BackEndAuditRecord>>(url, { headers, params }).pipe(
      map(results => new PagedResults(results, record => new AuditRecord(record))),
    );
  }

  private toQueryParamList<T extends {[key: number]: string}>(enumList: T[]) {
    return `${enumList.join(',').toLowerCase()}`;
  }

  private parseClient(client: BackEndClient) {
    switch (client.type) {
      case ClientType.SAML2:
        return new SamlClient(client as BackEndSamlClient);
      case ClientType.OAuth2:
        return new OauthClient(client as BackEndOauthClient);
      default:
        throw new Error(`Unknown client type ${client}`);
    }
  }
}
